import React, { useState, useCallback, useMemo, useEffect } from "react";

import { Box, Grid, ThemeProvider } from "@mui/material";

import GlobalCard from "../../newComponent/NewCardsComp/GlobalCardComp";
import InputSelect from "../../newComponent/themeprovider/textField/InputSelect";
import CustomNewButton from "../../newComponent/themeprovider/button/CustomNewButton";
import LoaderComp from "../../newComponent/LoaderComp/Loader";
import mainTheme from "../../newComponent/themeprovider/mainTheme/MainTheme";
import DayTabs from "./component/DayTabs";
import ViewMode from "./ViewMode";
import { useSnackbar } from "../../components/ui-component/snackbar/SnackbarContext";
import HeadingCard from "../../newComponent/NewCardsComp/HeadingCard";

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

/* ================= DEFAULT ================= */

const emptyLesson = {
  topic: "",
  objective: "",
  content: "",
  activity: "",
  homework: "",
  remarks: "",

  study_material: [],
    presentation_files: [],
};

/* ================= NORMALIZER ================= */

const normalizeLesson = (data = {}) => ({
  topic: data?.topic ?? "",
  objective: data?.objective ?? "",
  content: data?.content ?? "",
  activity: data?.activity ?? "",
  homework: data?.homework ?? "",
  remarks: data?.remarks ?? "",
    presentation_files: data?.presentation_file_obj || [],
  study_material: Array.isArray(data?.study_material_obj)
    ? data.study_material_obj
    : [],
});

/* ================= COMPONENT ================= */

const CenterAdminLessonPlanner = () => {
  const userId = localStorage.getItem("userId");

  const { showSnackbar } = useSnackbar();

  const [testType, setTestType] = useState("");
  const [testTypeList, setTestTypeList] = useState([]);

  const [duration, setDuration] = useState("");
  const [durationList, setDurationList] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState(null);

  const [trainer, setTrainer] = useState(null);
  const [trainerList, setTrainerList] = useState([]);

  const [days, setDays] = useState([]);
  const [lessons, setLessons] = useState([]);

  const [activeDayIndex, setActiveDayIndex] = useState(0);

  const [isLoading, setIsLoading] = useState(false);

  /* ================= FETCH TEST TYPE ================= */

  useEffect(() => {
    const fetchTestType = async () => {
      try {
        const res = await fetch(`${API_ENDPOINT}route/getalltesttype`);
        const data = await res.json();
        if (res.ok) setTestTypeList(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchTestType();
  }, []);

  /* ================= FETCH DURATION ================= */

  useEffect(() => {
    if (!testType) return;

    const fetchDuration = async () => {
      try {
        const res = await fetch(
          `${API_ENDPOINT}package/by-testtype/${testType}`,
        );
        const data = await res.json();
        if (res.ok) setDurationList(data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchDuration();
  }, [testType]);

  /* ================= FETCH TRAINER ================= */

  useEffect(() => {
    const fetchTrainer = async () => {
      try {
        const res = await fetch(`${API_ENDPOINT}trainer`);
        const data = await res.json();
        if (res.ok) setTrainerList(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchTrainer();
  }, []);

  /* ================= PACKAGE AUTO SET ================= */

  useEffect(() => {
    const selected = durationList.find((d) => d.duration === duration);
    if (selected) setSelectedDuration(selected);
  }, [duration]);

  /* ================= ACTIVE DAY ================= */

  const activeDay = useMemo(() => {
    return days[activeDayIndex] || null;
  }, [days, activeDayIndex]);

  /* ================= CURRENT LESSON ================= */

  const currentLesson = useMemo(() => {
    return lessons[activeDayIndex] || emptyLesson;
  }, [lessons, activeDayIndex]);

 
  /* ================= CREATE PLAN ================= */

  const handleCreatePlan = async () => {
    if (!duration || !testType || !trainer) {
      showSnackbar("Please select all required fields", "error");
      return;
    }

    setIsLoading(true);

    try {
      const res = await fetch(`${API_ENDPOINT}lesson-plan/lesson-plan-report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          duration,
          test_type_id: testType,
          package_id: selectedDuration?.id,
          user_id: Number(userId),
          trainer_id: trainer,
        }),
      });

      const data = await res.json();

      if (res.ok && data?.days) {
        const dayArr = data.days;

        setDays(dayArr);

        const lessonArr = dayArr.map((d) => normalizeLesson(d));

        setLessons(lessonArr);

        setActiveDayIndex(0);
      } else {
        showSnackbar(data.message, "error");
        setDays([]);
        setLessons([]);
        setActiveDayIndex(0);

      }
    } catch (err) {
      showSnackbar(err.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= FETCH DAY LESSON ================= */

  const handleFetchLesson = async () => {
    if (!activeDay?.plan_id) return;

    try {
      setIsLoading(true);

      const res = await fetch(`${API_ENDPOINT}lesson-plan-day/get-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          plan_id: activeDay.plan_id,
          day_number: activeDay.day_number,
        }),
      });

      const data = await res.json();

      if (res.ok && data?.data) {
        setLessons((prev) => {
          const updated = [...prev];

          updated[activeDayIndex] = normalizeLesson(data.data);

          return updated;
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= SINGLE API CALL FIX ================= */

  useEffect(() => {
    if (!activeDay?.plan_id) return;

    handleFetchLesson();
  }, [activeDay]);

  console.log("activeDay", activeDay);

  /* ================= UI ================= */

  return (
    <>
      <LoaderComp isLoading={isLoading} />
    <Box
  sx={{
    mb: 2,
    width: {
      xs: "100%",  // Mobile
      sm: "70%",   // Small tablets
      md: "50%",   // Tablets
      lg: "30%",   // Small laptops
      xl: "30%"    // Large screens
    }
  }}
>
  <HeadingCard label="Lesson Plan Report" />
</Box>

      <ThemeProvider theme={mainTheme}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <GlobalCard>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <InputSelect
                    label="Select Test Type"
                    value={testType}
                    onChange={(e) => {
                      setTestType(e.target.value);
                      setDuration("");
                    }}
                    options={testTypeList}
                  />
                </Grid>

                   <Grid item xs={12} md={3}>
                  <InputSelect
                    label=" Select Duration"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    options={
                      !testType
                        ? [
                            {
                              id: "no-test",
                              duration: "",
                              label: "Please select test type first",
                              disabled: true,
                            },
                          ]
                        : testType && durationList?.length===0
                        ? [
                            {
                              id: "no-duration",
                              duration: "",
                              label: "There is no duration available for this test type.",
                              disabled: true,
                            },
                        ]:
                        durationList
                    }
                    getOptionLabel={(o) => o.label || `${o.duration} Days`}
                    getValue={(o) => o.duration}
                    getOptionDisabled={(o) => o.disabled} // 👈 important
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <InputSelect
                    label="Select Trainer"
                    value={trainer}
                    onChange={(e) => setTrainer(e.target.value)}
                    options={trainerList}
                    getOptionLabel={(o) =>
                      `${o.first_name} ${o.last_name ?? ""}`
                    }
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <CustomNewButton
                    disabled={!testType || !duration || !trainer}
                    label="View Plan"
                    onClick={handleCreatePlan}
                  />
                </Grid>
              </Grid>
            </GlobalCard>
          </Grid>

          {days.length > 0 && (
            <Grid item xs={12}>
              <GlobalCard>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <DayTabs
                      days={days}
                      activeDayIndex={activeDayIndex}
                      setActiveDayIndex={setActiveDayIndex}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    {" "}
                    <ViewMode
                      currentLesson={currentLesson}
                      activeDay={activeDay}
                      // handleChange={handleChange}
                      activeDayIndex={activeDayIndex}
                    />
                  </Grid>
                </Grid>
              </GlobalCard>
            </Grid>
          )}
        </Grid>
      </ThemeProvider>
    </>
  );
};

export default CenterAdminLessonPlanner;
